import { cn } from '@/utilities/ui'
import React from 'react'
import RichText from '@/components/RichText'
import type { YouTubeEmbedBlock as YouTubeEmbedBlockProps } from '@/payload-types'

// Convert YouTube URLs to embed format
function getYouTubeEmbedUrl(url: string): string {
  try {
    const urlObj = new URL(url)

    // Handle youtube.com/watch?v= format
    if (urlObj.hostname.includes('youtube.com') && urlObj.pathname === '/watch') {
      const videoId = urlObj.searchParams.get('v')
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    }

    // Handle youtu.be/ format
    if (urlObj.hostname === 'youtu.be') {
      const videoId = urlObj.pathname.slice(1) // Remove leading slash
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    }

    // If already an embed URL, return as is
    if (urlObj.hostname.includes('youtube.com') && urlObj.pathname.startsWith('/embed/')) {
      return url
    }

    // Fallback to original URL
    return url
  } catch {
    // If URL parsing fails, return original
    return url
  }
}

export function YouTubeEmbed({
  url,
  caption,
  className,
}: YouTubeEmbedBlockProps & { className?: string }) {
  const embedUrl = url ? getYouTubeEmbedUrl(url) : ''

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        {embedUrl && (
          <iframe
            src={embedUrl}
            title="YouTube video player"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="w-full aspect-video"
          />
        )}
        {caption && (
          <div className={cn('relative pt-[10px] text-right')}>
            <RichText data={caption} enableProse={false} className="media-caption" />
          </div>
        )}
      </div>
    </div>
  )
}
